import type { SkyEvent } from '../astro/events';
import type { Place } from '../astro/frame';
import type { Shower } from '../astro/meteors';
import { AROUND, type Spot, addMonth, eventsOver, yearDone, yearMonth } from './eventscore';
import { saudiStarts } from './saudi';
import type { EventsIn, EventsOut } from './events.worker';

// A place's events for the sky calendar, "On now", Tonight and the reminders, worked out in the events worker so
// the sky keeps moving meanwhile. If the worker cannot start, fails or stops answering, the same sums run here
// instead, from where it got to (a year still a month at a time).

/** A worker that has not answered for this long has stopped: what it had left is worked out here. */
export const DEADLINE_MS = 15e3;

interface Job {
  /** An answer from the worker about this job. */
  got(m: EventsOut): void;
  /** The worker has gone: carry on here. */
  here(): void;
  timer?: ReturnType<typeof setTimeout>;
  /** When the deadline was last set. */
  since: number;
}
type DistOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

let worker: Worker | null = null;
/** Set once the worker could not start, failed or stopped answering: from then on everything is worked out here. */
let broken = false;
let lastId = 0;
const jobs = new Map<number, Job>();
/** When the page last went into the background (where a phone may pause the worker with it). */
let hiddenAt = 0;
const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

function start(): Worker | null {
  if (worker || broken) return worker;
  try {
    worker = new Worker(new URL('./events.worker.ts', import.meta.url), { type: 'module' });
  } catch {
    broken = true;
    return null;
  }
  worker.onmessage = (ev: MessageEvent<EventsOut>) => answer(ev.data);
  worker.onerror = () => fail();
  worker.onmessageerror = () => fail();
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => hidden() && (hiddenAt = Date.now()));
  return worker;
}

/** (Re)sets a job's deadline. Time spent in the background does not count against it. */
function arm(j: Job) {
  clearTimeout(j.timer);
  j.since = Date.now();
  j.timer = setTimeout(() => (hidden() || hiddenAt >= j.since ? arm(j) : fail()), DEADLINE_MS);
}

function answer(m: EventsOut) {
  const j = jobs.get(m.id);
  if (!j) return; // stopped since it was asked
  if (m.type === 'month' && !m.done) arm(j);
  else {
    clearTimeout(j.timer);
    jobs.delete(m.id);
  }
  j.got(m);
}

/** The worker cannot be used: the jobs still waiting carry on here, as does everything asked later. */
function fail() {
  if (broken) return;
  broken = true;
  worker?.terminate();
  worker = null;
  const left = [...jobs.values()];
  jobs.clear();
  for (const j of left) {
    clearTimeout(j.timer);
    j.here();
  }
}

/** Gives the worker a job; null when there is no worker, and the caller works it out here instead. */
function send(msg: DistOmit<Exclude<EventsIn, { type: 'cancel' }>, 'id'>, got: Job['got'], here: Job['here']): number | null {
  const w = start();
  if (!w) return null;
  const id = ++lastId;
  const j: Job = { got, here, since: 0 };
  jobs.set(id, j);
  arm(j);
  w.postMessage({ ...msg, id } as EventsIn);
  return id;
}

/** Stops a job: nothing more is taken from it, and the worker drops it if it is still going. */
function cancel(id: number | null) {
  const j = id === null ? undefined : jobs.get(id);
  if (!j) return;
  clearTimeout(j.timer);
  jobs.delete(id!);
  worker?.postMessage({ type: 'cancel', id } as EventsIn);
}

/** The place in plain numbers, with its time zone (as the Hijri date takes it: the place's, else the device's). */
const spot = (p: Spot & { timeZone?: string }): Spot => ({ lat: p.lat, lon: p.lon, height: p.height ?? 0, tz: p.tz ?? p.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone });
/** Saudi Arabia's decided month starts as the phone has them now (for the evenings of the 29th), and a short key for
 *  all of them, so worked-out events are kept only while the starts are the same (a correction to any month counts). */
const starts = () => saudiStarts.peek();
const startsKey = () => {
  const s = starts();
  let h = 0;
  for (const k of Object.keys(s).sort()) for (const c of `${k}${s[k]}`) h = (Math.imul(h, 31) + c.charCodeAt(0)) | 0;
  return (h >>> 0).toString(36);
};

// ---------- A stretch of weeks ----------

/** A place's events over `days` from `from`, for `got` once worked out (null if they could not be). Returns a function that stops the work. */
function range(p: Spot, from: number, days: number, showers: Shower[], bortle: number, got: (events: SkyEvent[] | null) => void): () => void {
  const at = spot(p);
  const s = starts();
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const failed = (why: unknown) => {
    console.warn('Sky events could not be worked out', why);
    got(null);
  };
  const here = () => {
    timer = setTimeout(() => {
      if (stopped) return;
      let events: SkyEvent[];
      try {
        events = eventsOver(at, from, days, showers, bortle, s);
      } catch (e) {
        return failed(e);
      }
      got(events);
    }, 0);
  };
  const id = send({ type: 'range', at, from, days, showers, bortle, starts: s }, (m) => (m.type === 'range' ? got(m.events) : m.type === 'failed' && failed(m.why)), here);
  if (id === null) here();
  return () => {
    stopped = true;
    clearTimeout(timer);
    cancel(id);
  };
}

/** A place's events over `days` from `from`, for `got` once worked out (none if they could not be). Returns a function that stops the work. */
export const eventsSoon = (p: Spot, from: number, days: number, showers: Shower[], bortle: number, got: (events: SkyEvent[]) => void) =>
  range(p, from, days, showers, bortle, (list) => got(list ?? []));

const nearCache = new Map<string, SkyEvent[]>();
const nearKey = (p: Place, now: number, bortle: number) => `${p.lat.toFixed(2)},${p.lon.toFixed(2)},${Math.round(bortle)},${Math.floor(now / 3600e3)},${startsKey()}`;
/** Kept for the rest of the hour, once the showers are in. */
function keepNear(key: string, list: SkyEvent[], showers: Shower[]) {
  if (!showers.length) return;
  nearCache.clear();
  nearCache.set(key, list);
}

/** Events at a place from 30 days ago to 45 days ahead (active showers, "On now", reminders), cached by the hour. Worked out at once, here. */
export function eventsAround(p: Place, now: number, showers: Shower[], bortle: number): SkyEvent[] {
  const key = nearKey(p, now, bortle);
  let list = nearCache.get(key);
  if (!list) {
    list = eventsOver(spot(p), now - AROUND.before, AROUND.days, showers, bortle, starts());
    keepNear(key, list, showers);
  }
  return list;
}

/** The same, from the events worker: `got` has the list once worked out (none if it could not be). Returns a function that stops the work. */
export function eventsAroundSoon(p: Place, now: number, showers: Shower[], bortle: number, got: (events: SkyEvent[]) => void): () => void {
  const key = nearKey(p, now, bortle);
  const hit = nearCache.get(key);
  if (hit) {
    got(hit);
    return () => {};
  }
  return range(p, now - AROUND.before, AROUND.days, showers, bortle, (list) => {
    if (list) keepNear(key, list, showers);
    got(list ?? []);
  });
}

/** The same as a promise, for reminders: null if they could not be worked out, which is not the same as none. */
export function eventsAroundLater(p: Place, now: number, showers: Shower[], bortle: number): Promise<SkyEvent[] | null> {
  const key = nearKey(p, now, bortle);
  const hit = nearCache.get(key);
  if (hit) return Promise.resolve(hit);
  return new Promise((done) => {
    range(p, now - AROUND.before, AROUND.days, showers, bortle, (list) => {
      if (list) keepNear(key, list, showers);
      done(list);
    });
  });
}

// ---------- The year ----------

const yearCache = new Map<string, SkyEvent[]>();
const keyFor = (p: Place, from: number, bortle: number) => `${p.lat.toFixed(2)},${p.lon.toFixed(2)},${Math.round(bortle)},${new Date(from).toISOString().slice(0, 13)},${startsKey()}`;

/**
 * A year of events at a place from `from`, a month at a time: `onChunk` gets the list so far (soonest first) after
 * each month, and `done` with the last; `onFail` if they could not be worked out. Returns a function that stops
 * the work.
 */
export function computeYear(p: Place, from: number, showers: Shower[], bortle: number, onChunk: (events: SkyEvent[], done: boolean) => void, onFail: () => void = () => {}): () => void {
  const key = keyFor(p, from, bortle);
  const cached = yearCache.get(key);
  if (cached) {
    onChunk(cached, true);
    return () => {};
  }
  const at = spot(p);
  const s = starts();
  const byId = new Map<string, SkyEvent>();
  let month = 0, stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const take = (events: SkyEvent[]) => {
    const list = addMonth(byId, events);
    const done = yearDone(++month);
    // Showers load just after start-up: a year worked out before then is shown but not kept.
    if (done && showers.length) yearCache.set(key, list);
    onChunk(list, done);
    return done;
  };
  const failed = (why: unknown) => {
    console.warn('The sky calendar could not be worked out', why);
    onFail();
  };
  // Here on the main thread, from the month the worker reached, one month per task so the page never stalls for long.
  const here = () => {
    const step = () => {
      if (stopped) return;
      let events: SkyEvent[];
      try {
        events = yearMonth(at, from, month, showers, bortle, s);
      } catch (e) {
        return failed(e);
      }
      if (!take(events)) timer = setTimeout(step, 0);
    };
    timer = setTimeout(step, 0);
  };
  const id = send({ type: 'year', at, from, showers, bortle, starts: s }, (m) => (m.type === 'month' ? take(m.events) : m.type === 'failed' && failed(m.why)), here);
  if (id === null) here();
  return () => {
    stopped = true;
    clearTimeout(timer);
    cancel(id);
  };
}
